import Dropdown, {
  DropdownItemProps,
} from "components/DesignSystem/Dropdown/Dropdown";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReactNode, useState } from "react";
import { Info } from "components/icons/Info";
import { CaretDown } from "components/icons/CaretDown";
import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { Transactions } from "types/Models/books";

type ActionDropdownProps = {
  showBaseActionItem?: boolean;
  transactionData: Transactions;
  Action: {
    RequestInfo?: (props: DropdownItemProps) => React.JSX.Element;
    SendToChat?: (props: DropdownItemProps) => React.JSX.Element;
    AddComment: (props: DropdownItemProps) => React.JSX.Element;
    AddInvoice?: (props: DropdownItemProps) => React.JSX.Element;
    Edit: (props: DropdownItemProps) => React.JSX.Element;
    Delete: (props: DropdownItemProps) => React.JSX.Element;
    SplitTransaction: (props: DropdownItemProps) => React.JSX.Element;
    EditSplitTransaction: (props: DropdownItemProps) => React.JSX.Element;
    DeleteSplitTransaction: (props: DropdownItemProps) => React.JSX.Element;
    ExcludeTransaction: (props: DropdownItemProps) => React.JSX.Element;
    RestoreTransaction: (props: DropdownItemProps) => React.JSX.Element;
    LinkInkleInvoice: (props: DropdownItemProps) => React.JSX.Element;
    UnlinkInkleInvoice: (props: DropdownItemProps) => React.JSX.Element;
    LinkInkleBill: (props: DropdownItemProps) => React.JSX.Element;
    UnlinkInkleBill: (props: DropdownItemProps) => React.JSX.Element;
    Duplicate?: (props: DropdownItemProps) => React.JSX.Element;
  };
};

type ActionItem = {
  component: ReactNode;
};

export const DisabledActionsSection = ({
  disabledItems,
  tooltipMessage,
}: {
  disabledItems: ReactNode[];
  tooltipMessage: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (disabledItems.length === 0) return null;

  return (
    <div className="t-max-w-72">
      <Dropdown.Separator />
      {isExpanded && (
        <>
          <div className="t-flex t-justify-center t-py-2 t-px-3 t-rounded-lg t-gap-1 t-w-full">
            <span className="t-flex t-text-text-60 t-items-center t-mt-0">
              <Info color="currentColor" stroke="1.2" size="14" />
            </span>
            <ToolTip text={tooltipMessage}>
              <div className="t-space-y-1 t-flex-1 t-min-w-0">
                <p className="t-text-body-sm t-text-text-30 t-mb-0 t-italic  t-line-clamp-1">
                  {tooltipMessage}
                </p>
              </div>
            </ToolTip>
          </div>
          {disabledItems}
          <Dropdown.Separator />
        </>
      )}
      <div
        className="t-px-3 t-py-2 t-cursor-pointer hover:t-bg-neutral-0 t-rounded t-flex t-items-center t-gap-1 t-w-full"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="t-text-body-sm t-text-purple-50 t-whitespace-nowrap">
          {isExpanded ? "View less" : "View more"}
        </span>
        <CaretDown
          className={classNames(
            "t-h-3 t-w-3 t-text-neutral-50 t-transition-transform",
            {
              "t-rotate-180": isExpanded,
            }
          )}
        />
      </div>
    </div>
  );
};

export const ActionDropdown = ({
  transactionData,
  Action,
  showBaseActionItem = true,
}: ActionDropdownProps) => {
  const { isCpa } = useRoleBasedView();

  const isManualTransaction = Boolean(
    transactionData?.transaction?.journal_entry
  );
  const isSplitTransaction = Boolean(
    transactionData?.split_transactions &&
      transactionData.split_transactions.length > 0
  );
  const isExcluedTransaction =
    transactionData?.transaction?.status === "EXCLUDED";

  const isCreditCard = transactionData?.transaction?.is_credit_card;
  const isCredit =
    transactionData?.transaction?.amount &&
    transactionData.transaction.amount > 0;
  const isDebit =
    transactionData?.transaction?.amount &&
    transactionData.transaction.amount < 0;
  const hasLinkedInvoice = transactionData?.invoices?.some(
    ({ has_inkle_invoice }) => has_inkle_invoice
  );
  const hasLinkedBill = transactionData?.invoices?.some(
    ({ has_inkle_bill }) => has_inkle_bill
  );

  const isInvoiceLinked = Boolean(
    isCredit && !isCreditCard && hasLinkedInvoice
  );
  const isInvoiceLinkable = Boolean(
    isCredit &&
      !isCreditCard &&
      (hasLinkedInvoice ? (transactionData?.available_amount ?? 0) > 0 : true)
  );
  const isBillLinked = Boolean(isDebit && hasLinkedBill);
  const isBillLinkable = Boolean(
    isDebit &&
      (hasLinkedBill ? (transactionData?.available_amount ?? 0) > 0 : true)
  );

  const invoiceTransaction = Boolean(hasLinkedInvoice);
  const isLinkedTransaction = Boolean(
    transactionData?.transaction?.linked_transaction
  );

  const buildActions = (): {
    enabled: ReactNode[];
    disabled: ActionItem[];
  } => {
    const enabled: ReactNode[] = [];
    const disabled: ActionItem[] = [];

    if (isExcluedTransaction) {
      enabled.push(<Action.RestoreTransaction key="restore" />);
      if (isManualTransaction) {
        enabled.push(<Action.Delete key="delete" />);
      }
      return { enabled, disabled };
    }

    if (isCpa && showBaseActionItem && Action.RequestInfo) {
      enabled.push(<Action.RequestInfo key="request-info" />);
    }

    if (showBaseActionItem) {
      if (Action.SendToChat) {
        enabled.push(<Action.SendToChat key="send-to-chat" />);
      }

      enabled.push(<Action.AddComment key="add-comment" />);

      if (Action.AddInvoice) {
        enabled.push(<Action.AddInvoice key="add-invoice" />);
      }
    }

    if (Action.Duplicate) {
      if (isSplitTransaction) {
        disabled.push({
          component: <Action.Duplicate key="duplicate" disabled />,
        });
      } else {
        enabled.push(<Action.Duplicate key="duplicate" />);
      }
    }

    if (isInvoiceLinkable && !isSplitTransaction && showBaseActionItem) {
      enabled.push(<Action.LinkInkleInvoice key="link-inkle-invoice" />);
    }

    if (isSplitTransaction && !isInvoiceLinked) {
      disabled.push({
        component: (
          <Action.LinkInkleInvoice key="link-inkle-invoice" disabled />
        ),
      });
    }

    if (isInvoiceLinked) {
      enabled.push(<Action.UnlinkInkleInvoice key="unlink-inkle-invoice" />);
    }

    if (isBillLinkable && !isSplitTransaction && showBaseActionItem) {
      enabled.push(<Action.LinkInkleBill key="link-inkle-bill" />);
    }

    if (isSplitTransaction && !isBillLinked) {
      disabled.push({
        component: <Action.LinkInkleBill key="link-inkle-bill" disabled />,
      });
    }

    if (isBillLinked) {
      enabled.push(<Action.UnlinkInkleBill key="unlink-inkle-bill" />);
    }

    if (isSplitTransaction && !invoiceTransaction) {
      if (isInvoiceLinked) {
        disabled.push({
          component: <Action.EditSplitTransaction key="edit-split" disabled />,
        });
        disabled.push({
          component: (
            <Action.DeleteSplitTransaction key="delete-split" disabled />
          ),
        });
      } else {
        enabled.push(<Action.EditSplitTransaction key="edit-split" />);
        enabled.push(<Action.DeleteSplitTransaction key="delete-split" />);
      }
    }

    if (!isSplitTransaction && !invoiceTransaction) {
      enabled.push(<Action.SplitTransaction key="split" />);
    }

    if (isManualTransaction) {
      if (isSplitTransaction || isLinkedTransaction) {
        disabled.push({
          component: <Action.Edit key="edit" disabled />,
        });
      } else {
        enabled.push(<Action.Edit key="edit" />);
      }
    }

    if (isInvoiceLinked || isLinkedTransaction) {
      disabled.push({
        component: <Action.ExcludeTransaction key="exclude" disabled />,
      });
    } else {
      enabled.push(<Action.ExcludeTransaction key="exclude" />);
    }

    if (isManualTransaction) {
      if (isSplitTransaction || isLinkedTransaction) {
        disabled.push({
          component: <Action.Delete key="delete" disabled />,
        });
      } else {
        enabled.push(<Action.Delete key="delete" />);
      }
    }

    return { enabled, disabled };
  };

  const { enabled, disabled } = buildActions();

  const disabledComponents = disabled.map((item) => item.component);
  let tooltipMessage = "";

  if (isSplitTransaction)
    tooltipMessage = "Actions unavailable because this is a split transaction.";

  if (isInvoiceLinked)
    tooltipMessage =
      "Actions unavailable because transaction is linked to invoice.";

  if (isLinkedTransaction)
    tooltipMessage =
      "Actions unavailable because this is a linked transaction.";

  return (
    <>
      {enabled}
      <DisabledActionsSection
        disabledItems={disabledComponents}
        tooltipMessage={tooltipMessage}
      />
    </>
  );
};
